// ─── src/components/dashboard/MentorCharts.tsx ───────────────────────────────
import type { FC } from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";
import type { ChartEntry } from "../../types/stats";

// ── Colors ────────────────────────────────────────────────────────────────────
// Palettes lead with the main website's emerald-green brand.
const TOPIC_COLORS = [
  "#00DC92", "#10b981", "#6366f1", "#ec4899",
  "#34d399", "#f59e0b", "#3b82f6", "#8b5cf6",
  "#ef4444", "#14a085", "#84cc16", "#06b6d4",
];

const CITY_COLORS = [
  "#00DC92", "#10b981", "#34d399", "#6366f1",
  "#ec4899", "#14a085", "#f59e0b", "#3b82f6",
  "#8b5cf6", "#ef4444",
];

const FREQ_COLORS: Record<string, string> = {
  "on-demand": "#00DC92",
  monthly:     "#10b981",
  quarterly:   "#6366f1",
};

const MODE_COLORS: Record<string, string> = {
  "Online only": "#10b981",
  "Onsite only": "#00DC92",
  "Both":        "#34d399",
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const CustomTooltip = ({
  active, payload,
}: {
  active?: boolean;
  payload?: { payload: ChartEntry; color: string }[];
}) => {
  if (!active || !payload?.length) return null;
  const { name, value, percentage } = payload[0].payload;
  return (
    <div className="bg-brand-card border border-brand-border rounded-xl
                    px-4 py-3 shadow-xl text-sm">
      <p className="text-white font-semibold capitalize">{name}</p>
      <p className="text-brand-muted mt-0.5">
        {value} mentor{value !== 1 ? "s" : ""}
        <span className="text-brand-orange ml-2">({percentage}%)</span>
      </p>
    </div>
  );
};

// ── Donut ─────────────────────────────────────────────────────────────────────
const Donut: FC<{ data: ChartEntry[]; colors: string[] }> = ({ data, colors }) => (
  <ResponsiveContainer width="100%" height="100%">
    <PieChart>
      <Pie
        data={data}
        cx="50%" cy="50%"
        innerRadius={55} outerRadius={95}
        paddingAngle={3}
        dataKey="value"
        stroke="none"
      >
        {data.map((_, i) => (
          <Cell key={i} fill={colors[i % colors.length]} />
        ))}
      </Pie>
      <Tooltip content={<CustomTooltip />} />
    </PieChart>
  </ResponsiveContainer>
);

// ── Legend ────────────────────────────────────────────────────────────────────
const Legend: FC<{ data: ChartEntry[]; colors: string[] }> = ({ data, colors }) => (
  <div className="flex-1 w-full">
    <div className="flex items-center justify-between text-xs text-brand-muted
                    uppercase tracking-wider mb-3 px-1">
      <span>Label</span>
      <div className="flex gap-4">
        <span>Count</span>
        <span className="w-10 text-right">Share</span>
      </div>
    </div>
    <ul className="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1">
      {data.map((entry, i) => (
        <li key={entry.name} className="flex items-center justify-between gap-3 text-sm">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: colors[i % colors.length] }}
            />
            <span className="text-brand-muted truncate capitalize">{entry.name}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-white font-medium">{entry.value}</span>
            <span className="text-brand-muted text-xs w-10 text-right">{entry.percentage}%</span>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

// ── Chart card wrapper ────────────────────────────────────────────────────────
const ChartCard: FC<{
  title:    string;
  subtitle: string;
  data:     ChartEntry[];
  colors:   string[];
}> = ({ title, subtitle, data, colors }) => (
  <div className="card p-6 flex-1 min-w-0">
    <h2 className="text-white font-semibold text-base mb-1">{title}</h2>
    <p className="text-brand-muted text-sm mb-6">{subtitle}</p>
    <div className="flex flex-col lg:flex-row items-center gap-6">
      <div className="w-full lg:w-56 h-56 shrink-0">
        <Donut data={data} colors={colors} />
      </div>
      <Legend data={data} colors={colors} />
    </div>
  </div>
);

// ── Public exports ────────────────────────────────────────────────────────────

export const MentorTopicsChart: FC<{ data: ChartEntry[] }> = ({ data }) => {
  if (!data.length) return null;
  return (
    <ChartCard
      title="Mentors by Topic"
      subtitle={`${data.length} topic${data.length !== 1 ? "s" : ""} covered`}
      data={data}
      colors={TOPIC_COLORS}
    />
  );
};

export const MentorCitiesChart: FC<{ data: ChartEntry[] }> = ({ data }) => {
  if (!data.length) return null;
  return (
    <ChartCard
      title="Mentors by City"
      subtitle={`Coverage across ${data.length} location${data.length !== 1 ? "s" : ""}`}
      data={data}
      colors={CITY_COLORS}
    />
  );
};

export const MentorFrequencyChart: FC<{ data: ChartEntry[]; total?: number }> = ({
  data, total,
}) => {
  if (!data.length) return null;
  const colors = data.map((d) => FREQ_COLORS[d.name] ?? "#6366f1");
  const t = total ?? data.reduce((acc, e) => acc + e.value, 0);
  return (
    <ChartCard
      title="Mentors by Availability"
      subtitle={`${t} mentor${t !== 1 ? "s" : ""} total`}
      data={data}
      colors={colors}
    />
  );
};

export const MentorModeChart: FC<{ data: ChartEntry[] }> = ({ data }) => {
  if (!data.length) return null;
  const colors = data.map((d) => MODE_COLORS[d.name] ?? "#6366f1");
  return (
    <ChartCard
      title="Mentors by Mode"
      subtitle="Online vs Onsite vs Both"
      data={data}
      colors={colors}
    />
  );
};
